import { useCallback, useEffect, useState } from 'react';
import { useFolderTree, useFolderHierarchy, SentryBreadcrumbCategory, useStatsQueueSender, createFolderBusinessFilter } from '@keepeek/commons';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { currentFolderIdState, currenFolderRootIdState, foldersVisibleState, foldersState } from './atoms.js';

var useBreadcrumb = function (_a) {
    var key = _a.key; _a.resetSearch; var initFolderId = _a.initFolderId, initFolderRootId = _a.initFolderRootId, _c = _a.initialShowFolders, initialShowFolders = _c === void 0 ? false : _c, clientIp = _a.clientIp, showSubFolderLink = _a.showSubFolderLink, rootFolderIds = _a.rootFolderIds, setActiveFilters = _a.setActiveFilters, sentryCallback = _a.sentryCallback;
    var t = useTranslation().t;
    var _d = useRecoilState(currentFolderIdState(key)), currentFolderId = _d[0], setCurrentFolderId = _d[1];
    var _e = useRecoilState(currenFolderRootIdState(key)), currentFolderRootId = _e[0], setCurrentFolderRootId = _e[1];
    var _f = useRecoilState(foldersVisibleState(key)), foldersVisible = _f[0], setFoldersVisible = _f[1];
    var _g = useFolderTree({
        folderId: currentFolderId,
        rootFolderIds: rootFolderIds
    }), folderTree = _g.folderTree, loadingFolderTree = _g.loading;
    var _h = useFolderHierarchy({
        folderId: currentFolderId,
        folderRootId: currentFolderRootId
    }), folderTrees = _h.folderTrees, loadingFolders = _h.loading;
    var folderFromId = useCallback(function (folderId) { var _a; return (folderTree === null || folderTree === void 0 ? void 0 : folderTree.children) && ((_a = folderTree === null || folderTree === void 0 ? void 0 : folderTree.children) === null || _a === void 0 ? void 0 : _a.find(function (f) { return f.id === folderId; })); }, [folderTree === null || folderTree === void 0 ? void 0 : folderTree.children]);
    // Init effect
    useEffect(function () {
        setCurrentFolderId(initFolderId);
        setCurrentFolderRootId(initFolderRootId);
        setFoldersVisible(initialShowFolders);
    }, [
        initFolderId,
        initFolderRootId,
        initialShowFolders,
        setCurrentFolderId,
        setCurrentFolderRootId,
        setFoldersVisible,
    ]);
    var handleHideButton = useCallback(function (foldersVisible) {
        sentryCallback &&
            sentryCallback({
                category: SentryBreadcrumbCategory.USER_INTERACTION,
                message: "useBreadcrumb - handleHideButton"
            });
        setCurrentFolderId(function (oldState) { return (oldState ? initFolderId : oldState); });
        setFoldersVisible(!foldersVisible);
    }, [initFolderId, sentryCallback, setCurrentFolderId, setFoldersVisible]);
    var sendFolderSearchHitToQueue = useStatsQueueSender(clientIp).sendFolderSearchHitToQueue;
    var _handleFilterUpdate = useCallback(function (_a) {
        var _b, _c;
        var folderId = _a.folderId, _d = _a.folderTitle, folderTitle = _d === void 0 ? "" : _d, _e = _a.treeMediaCount, treeMediaCount = _e === void 0 ? -1 : _e, extraParams = _a.extraParams;
        var givenFolderFromId = folderFromId(folderId);
        var resultCount = (_b = givenFolderFromId === null || givenFolderFromId === void 0 ? void 0 : givenFolderFromId.treeMediaCount) !== null && _b !== void 0 ? _b : treeMediaCount;
        sendFolderSearchHitToQueue({ folderId: folderId, resultCount: resultCount });
        setActiveFilters([
            createFolderBusinessFilter(folderId, folderTitle || ((_c = givenFolderFromId === null || givenFolderFromId === void 0 ? void 0 : givenFolderFromId.title) !== null && _c !== void 0 ? _c : ""), t("business-filter.advanced.folder.label")),
        ], extraParams);
    }, [folderFromId, sendFolderSearchHitToQueue, setActiveFilters, t]);
    var loading = loadingFolderTree || loadingFolders;
    // Keep a state in sync to avoid loading state while receiveing new hierarchy
    var _j = useRecoilState(foldersState(key)), folders = _j[0], setFolders = _j[1];
    useEffect(function () {
        if (!loadingFolderTree && folderTrees) {
            setFolders(folderTrees.map(function (ft) { return ft.folder; }));
        }
    }, [folderTrees, loadingFolderTree, setFolders]);
    // This will open the folder with at least one sub folder to show
    // It will look into the current, and in case of a end folder, will look on the one above
    var _k = useState(), askOpenFoldersWithSeeAll = _k[0], setAskOpenFoldersWithSeeAll = _k[1];
    useEffect(function () {
        var _a, _b;
        // Check only when needed
        if (askOpenFoldersWithSeeAll) {
            if (!folderTree) {
                // If we don't have a folder opened yet, open it first
                setFoldersVisible(!!askOpenFoldersWithSeeAll);
            }
            else if (((_b = (_a = folderTree === null || folderTree === void 0 ? void 0 : folderTree.folder) === null || _a === void 0 ? void 0 : _a.subFolderCount) !== null && _b !== void 0 ? _b : 0) > 0) {
                // If we have sub folder then stop looking for parent
                setAskOpenFoldersWithSeeAll(undefined);
            }
            else if (folders === null || folders === void 0 ? void 0 : folders[(folders === null || folders === void 0 ? void 0 : folders.length) - 2]) {
                // If we don't have subFolder on this one, look on the parent
                setCurrentFolderId(folders === null || folders === void 0 ? void 0 : folders[(folders === null || folders === void 0 ? void 0 : folders.length) - 2].id);
            }
            else {
                // In case of fail of case above, don't look too much and stop the process
                setAskOpenFoldersWithSeeAll(undefined);
            }
        }
    }, [askOpenFoldersWithSeeAll, folderTree, folders, setCurrentFolderId, setFoldersVisible]);
    var openFolder = function (folderId) {
        sentryCallback &&
            sentryCallback({
                category: SentryBreadcrumbCategory.USER_INTERACTION,
                message: "useBreadcrumb - openFolder: ".concat(folderId)
            });
        setCurrentFolderId(folderId);
        setFoldersVisible(true);
    };
    var search = function (_a) {
        var folderId = _a.folderId, extraParams = _a.extraParams, folderTitle = _a.folderTitle, treeMediaCount = _a.treeMediaCount;
        sentryCallback &&
            sentryCallback({
                category: SentryBreadcrumbCategory.USER_INTERACTION,
                message: "useBreadcrumb - search: ".concat(folderId, ", extraParams: ").concat(JSON.stringify(extraParams))
            });
        if (folderId === initFolderId) {
            setAskOpenFoldersWithSeeAll(folderId);
        }
        var folderFromIdRes = folderFromId(folderId);
        _handleFilterUpdate({
            folderId: folderId,
            folderTitle: folderTitle || (folderFromIdRes === null || folderFromIdRes === void 0 ? void 0 : folderFromIdRes.title),
            treeMediaCount: treeMediaCount || (folderFromIdRes === null || folderFromIdRes === void 0 ? void 0 : folderFromIdRes.treeMediaCount),
            extraParams: extraParams
        });
    };
    var handleFolderClick = function (_a) {
        var folderId = _a.folderId, extraParams = _a.extraParams, _b = _a.changeDataViewOnClick, changeDataViewOnClick = _b === void 0 ? false : _b, folderTitle = _a.folderTitle, treeMediaCount = _a.treeMediaCount, _c = _a.callback, callback = _c === void 0 ? function () { } : _c;
        var folder = folderFromId(folderId);
        if (showSubFolderLink || changeDataViewOnClick || !(folder === null || folder === void 0 ? void 0 : folder.hasChildren)) {
            var context = "search";
            callback(context);
            search({ folderId: folderId, extraParams: extraParams, folderTitle: folderTitle, treeMediaCount: treeMediaCount });
        }
        else {
            var context = "openFolder";
            callback(context);
            openFolder(folderId);
        }
    };
    return {
        openFolder: openFolder,
        search: search,
        folderFromId: folderFromId,
        handleFolderClick: handleFolderClick,
        handleHideButton: handleHideButton,
        loading: loading,
        foldersVisible: foldersVisible,
        folderTree: folderTree,
        folders: folders,
        loadingFolderTree: loadingFolderTree,
        loadingFolders: loadingFolders
    };
};

export { useBreadcrumb };
//# sourceMappingURL=hooks.js.map
